import { useCallback, useEffect, useRef } from "react";
import { useUserState } from "./useUserState";
import { useGeotabClient } from "@/components/GeotabClientContext";

/** A group reduced to what has to be stored to find it again. */
interface StoredGroup {
  id: string;
  name?: string;
}

interface GroupFilterSettings extends Record<string, unknown> {
  /** Whether the user asked for their group filter to be remembered. Off unless they say so. */
  remember: boolean;
  /** Empty while not remembering, so opting out leaves nothing behind. */
  groups: StoredGroup[];
}

const NOTHING_REMEMBERED: GroupFilterSettings = { remember: false, groups: [] };

export interface RememberedGroups<TGroup> {
  remember: boolean;
  /** False until the stored record is known; the switch does nothing before that. */
  isReady: boolean;
  /** Turning this on stores what is selected now; turning it off forgets it. */
  setRemember: (remember: boolean) => void;
  /** Call whenever the user changes the selection. */
  recordSelection: (groups: TGroup[]) => void;
}

interface Options<TGroup> {
  /** Every group the user could pick, once loaded. */
  available: TGroup[] | undefined;
  /** What is selected right now, so turning the switch on can store it. */
  selected: TGroup[] | undefined;
  /** Called at most once, with the groups to put back. */
  onRestore: (groups: TGroup[]) => void;
}

/** Stored under this `userState` key (reserved in the user-state spec). */
const STATE_KEY = "groupFilter";

/**
 * Remembers the group filter for the signed-in user, if they ask for it.
 *
 * Off by default, and deliberately so: the group filter decides which vehicles every page in the
 * add-in shows, and a filter that reappears by itself is indistinguishable from a fleet that has
 * lost vehicles. It is only ever restored because the user ticked a box saying to.
 *
 * Not `useRememberedSelection`, which remembers one option out of a list and falls back to the
 * first: here nothing selected means every vehicle, so falling back to the first group would
 * hide the rest of the fleet.
 *
 * @param options - See {@link Options}.
 */
export function useRememberedGroups<TGroup extends { id: string; name?: string }>(
  { available, selected, onRestore }: Options<TGroup>,
): RememberedGroups<TGroup> {
  const { value: settings, setValue: setSettings, isReady } = useUserState<GroupFilterSettings>(
    STATE_KEY,
    NOTHING_REMEMBERED,
  );

  // A session switch keeps this hook mounted but changes whose filter it is, so what has been
  // restored (or taken over by the user) is tracked per user, not once per mount.
  const { activeSessionId, me, meResolved } = useGeotabClient();
  const owner = `${activeSessionId}::${meResolved ? (me?.id ?? "") : ""}`;
  const handledFor = useRef<string | null>(null);
  const pendingSelection = useRef<{ owner: string; groups: StoredGroup[] } | null>(null);

  useEffect(() => {
    if (handledFor.current === owner || !isReady) {
      return;
    }
    // The stored record and the group list arrive from separate requests, so wait for the list
    // before deciding a remembered group no longer exists.
    if (!available || available.length === 0) {
      return;
    }

    handledFor.current = owner;

    // Geotab's own top-bar group filter (seeded from state.getGroupFilter()) must take
    // precedence over a remembered default from a previous session. With no filter chosen
    // Geotab reports the company root, which filters nothing.
    if (hasNarrowingFilter(selected)) {
      return;
    }

    if (!settings.remember || settings.groups.length === 0) {
      return;
    }

    const stillThere = settings.groups
      .map(stored => available.find(group => group.id === stored.id))
      .filter((group): group is TGroup => !!group);

    // Nothing left to put back — every remembered group has been deleted since. Restoring an
    // empty selection would mean the same as the unfiltered state the page is already in, so
    // there is nothing to do and no reason to write to the shared state.
    if (stillThere.length > 0) {
      onRestore(stillThere);
    }
  }, [available, isReady, onRestore, owner, selected, settings.groups, settings.remember]);

  // A selection made before the stored record is known cannot be judged against `remember`
  // yet, so it waits here and is stored once the record has been applied.
  useEffect(() => {
    const pending = pendingSelection.current;
    if (!isReady || !pending) {
      return;
    }
    pendingSelection.current = null;
    // Made under another user: not this user's to store.
    if (pending.owner !== owner) {
      return;
    }
    const { groups } = pending;
    setSettings(current => (current.remember ? { ...current, groups } : current));
  }, [isReady, owner, setSettings]);

  const recordSelection = useCallback(
    (groups: TGroup[]) => {
      if (!isReady) {
        pendingSelection.current = { owner, groups: groups.map(toStored) };
        // The user has taken charge of the filter; nothing should be put back over it later.
        handledFor.current = owner;
        return;
      }
      setSettings(current =>
        current.remember ? { ...current, groups: groups.map(toStored) } : current,
      );
    },
    [isReady, owner, setSettings],
  );

  const setRemember = useCallback(
    (remember: boolean) => {
      if (!isReady) {
        return;
      }
      // Storing the current selection as the switch goes on saves the user from re-picking what
      // is already on screen; clearing it as the switch goes off leaves nothing behind.
      setSettings(current => ({
        ...current,
        remember,
        groups: remember ? (selected ?? []).map(toStored) : [],
      }));
      // The user has taken charge of this; nothing should be put back over their choice later.
      handledFor.current = owner;
    },
    [isReady, owner, selected, setSettings],
  );

  return { remember: settings.remember, isReady, setRemember, recordSelection };
}

const ROOT_GROUP_ID = "GroupCompanyId";

function hasNarrowingFilter(selected: { id: string }[] | undefined): boolean {
  return !!selected && selected.some(group => group.id !== ROOT_GROUP_ID);
}

function toStored(group: { id: string; name?: string }): StoredGroup {
  return { id: group.id, name: group.name };
}
